import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import {
  Store,
  TrendingUp,
  Package,
  PhoneCall,
  ShieldCheck,
  FileText,
  BarChart3,
  Sparkles,
  Upload,
  Cpu,
  Smartphone,
} from "lucide-react";
import { usePortalLanguage, tLang } from "@/contexts/PortalLanguageContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const CDN = "https://pub-f22312f1902b4c5b98d85bf07b70d4e9.r2.dev";

// Brand tokens
const EMBER = "#FF6B35";
const TEAL  = "#00BFA5";
const NAVY  = "#1A1A2E";

export default function Retailers() {
  const { t } = usePortalLanguage();
  const { pathname } = useLocation();
  const locale = pathname.startsWith("/fr") ? "fr" : pathname.startsWith("/es") ? "es" : "en";

  // ─── Section data ────────────────────────────────────────────────────────────

  const onboardingSteps = [
    {
      icon: Upload,
      title: t("retailers.onboarding.step1.title", "Upload your product catalogue"),
      desc:  t("retailers.onboarding.step1.desc",  "Add your fireplaces, grills, and patio products to the Fyrris portal. Basic product photos and specs are all you need to start."),
    },
    {
      icon: Cpu,
      title: t("retailers.onboarding.step2.title", "We handle the AR processing"),
      desc:  t("retailers.onboarding.step2.desc",  "Our team builds and optimizes a photorealistic 3D model for every product. No technical work required on your end."),
    },
    {
      icon: Smartphone,
      title: t("retailers.onboarding.step3.title", "Customers visualize instantly"),
      desc:  t("retailers.onboarding.step3.desc",  "Shoppers place your products in their home via AR — in-store on your showroom iPad or at home on their own device."),
    },
  ];

  const pricingTailored = t("retailers.pricing.tailored", "Pricing tailored to your store");
  const pricingOrderForm = t("retailers.pricing.orderFormLine", "Set out in your Order Form");

  const pricingPlans = [
    {
      key:      "founding",
      name:     t("retailers.pricing.founding.name",    "Founding Retailer"),
      badge:    t("retailers.pricing.founding.badge",   "First cohort"),
      subtext:  t("retailers.pricing.founding.subtext", "A limited first cohort of retailers"),
      features: [
        t("retailers.pricing.founding.f1", "AR visualization widget (embed on website)"),
        t("retailers.pricing.founding.f2", "Manufacturer product catalogue access"),
        t("retailers.pricing.founding.f3", "Basic admin portal"),
        t("retailers.pricing.founding.f4", "Email support"),
        t("retailers.pricing.founding.f5", "Rate locked for the term of your agreement"),
        t("retailers.pricing.founding.f6", "Early access to new features"),
        t("retailers.pricing.founding.f7", "Direct line to the founder"),
      ],
      cta:      t("retailers.pricing.founding.cta", "Book a Demo"),
      ctaHref:  "/retailers/demo",
      dotColor: EMBER,
      ctaColor: EMBER,
      badgeClass: "bg-orange-100 text-orange-700",
    },
    {
      key:      "standard",
      name:     t("retailers.pricing.standard.name",    "Standard"),
      badge:    t("retailers.pricing.standard.badge",   "Most Popular"),
      subtext:  "",
      features: [
        t("retailers.pricing.standard.f1", "Everything in Founding"),
        t("retailers.pricing.standard.f2", "Full manufacturer catalogue"),
        t("retailers.pricing.standard.f3", "AR usage analytics (when available)"),
        t("retailers.pricing.standard.f4", "Qualified lead data"),
        t("retailers.pricing.standard.f5", "Priority support"),
      ],
      cta:      t("retailers.pricing.standard.cta", "Book a Demo"),
      ctaHref:  "/retailers/demo",
      dotColor: TEAL,
      ctaColor: TEAL,
      badgeClass: "bg-teal-100 text-teal-700",
    },
    {
      key:      "growth",
      name:     t("retailers.pricing.growth.name",    "Growth"),
      badge:    t("retailers.pricing.growth.badge",   "Coming Soon"),
      subtext:  t("retailers.pricing.growth.subtext", "White-label ready · multi-location"),
      features: [
        t("retailers.pricing.growth.f1", "Everything in Standard"),
        t("retailers.pricing.growth.f2", "White-label / branded experience"),
        t("retailers.pricing.growth.f3", "Advanced analytics & reporting"),
        t("retailers.pricing.growth.f4", "Multi-location support"),
        t("retailers.pricing.growth.f5", "Dedicated account manager"),
      ],
      cta:      t("retailers.pricing.growth.cta", "Book a Demo"),
      ctaHref:  "/retailers/demo",
      dotColor: "#9CA3AF",
      ctaColor: "#374151",
      badgeClass: "bg-gray-100 text-gray-500",
    },
  ];

  const faqItems = [
    {
      q: t("retailers.faq.q1", "What products can I onboard?"),
      a: t("retailers.faq.a1", "Any physical product in the HPB space — fireplaces, wood stoves, gas inserts, BBQ grills, patio furniture, outdoor kitchens, and more. If it has dimensions, we can build an AR model for it."),
    },
    {
      q: t("retailers.faq.q2", "How long does setup take?"),
      a: t("retailers.faq.a2", "Most retailers are live within 2–3 weeks. We handle all the 3D processing. You submit your product list and photos; we take it from there."),
    },
    {
      q: t("retailers.faq.q3", "Do I need technical staff?"),
      a: t("retailers.faq.a3", "No. The Fyrris portal is built for non-technical users. If you can update a spreadsheet, you can manage your full product catalogue and locations."),
    },
    {
      q: t("retailers.faq.q4", "What happens after the founding cohort is full?"),
      a: t("retailers.faq.a4", "Founding is a limited first cohort. Retailers who join later still get pricing tailored to their store, set out in their Order Form. Any rate commitment is whatever your Order Form states."),
    },
  ];

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="bg-gray-950 text-gray-100">
      <Helmet>
        <title>{tLang(locale, "retailers.meta.title", "Fyrris for HPB Retailers")}</title>
        <meta name="description" content={tLang(locale, "retailers.meta.description")} />
        <meta name="keywords" content="HPB specialty retailer, fireplace retailer software, patio furniture showroom, BBQ grill store, AR shopping platform, outdoor living retailer" />
        <meta property="og:title" content={tLang(locale, "retailers.meta.ogTitle")} />
        <meta property="og:description" content={tLang(locale, "retailers.meta.ogDescription")} />
        <meta property="og:image" content="https://www.fyrris.com/fyrris-social-banner.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={tLang(locale, "retailers.meta.ogTitle")} />
        <meta name="twitter:description" content={tLang(locale, "retailers.meta.ogDescription")} />
      </Helmet>

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden pt-12 pb-20 md:pt-14 md:pb-24 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: NAVY }}
      >
        {/* Hero image */}
        <img
          src={`${CDN}/hero/Retail%20Page%20-%20Hero.png`}
          alt="HPB specialty retailer showroom with fireplace and patio displays"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-[#1A1A2E]/70 pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto">
          <div
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold mb-6"
            style={{ backgroundColor: `${EMBER}22`, color: EMBER }}
          >
            <Store className="w-4 h-4" />
            {t("retailers.hero.eyebrow", "For HPB Retailers")}
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white mb-6">
            {t("retailers.hero.headline1", "Turn interest into in-store sales")}
            {/* Explicit space: the span below is display:block so this changes
                nothing visually, but without it the H1's *text content* reads
                "in-store saleswith AR" to screen readers and to crawlers that
                extract text rather than render it. */}
            {" "}
            <span className="block mt-2 text-3xl md:text-4xl lg:text-5xl font-semibold" style={{ color: EMBER }}>
              {t("retailers.hero.headline2", "with AR that answers every doubt before the visit.")}
            </span>
          </h1>

          <p className="text-gray-300 text-lg md:text-xl max-w-3xl mb-10 leading-relaxed">
            {t(
              "retailers.hero.subhead",
              "Fyrris helps shoppers preview fireplaces and patio products at home — then guides them to your store to buy. Fewer doubts, fewer returns, more confident purchases."
            )}
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/retailers/demo"
              className="inline-flex items-center gap-2 rounded-full px-8 py-3 font-medium text-white whitespace-nowrap transition hover:opacity-90"
              style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.3)" }}
            >
              {t("retailers.hero.cta", "Book a Demo")}
              <Sparkles className="w-4 h-4" />
            </Link>
            <Link
              to="/retailers/pricing"
              className="inline-flex items-center gap-2 rounded-full px-8 py-3 font-medium text-gray-300 border border-gray-600 transition hover:border-gray-400 hover:text-white"
            >
              {t("retailers.hero.ctaSecondary", "View pricing")}
            </Link>
          </div>
        </div>
      </section>

      {/* ── BENEFITS GRID ────────────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center">
            Why retailers join Fyrris
          </h2>

          <div className="mt-10 md:mt-12 grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <TrendingUp className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">More qualified foot traffic</h3>
              <p className="mt-2 text-gray-300 text-sm">
                Shoppers explore products in their space, then use geolocation
                to find the closest store that carries them. You meet customers
                who already know what they want.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <Package className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Fewer returns</h3>
              <p className="mt-2 text-gray-300 text-sm">
                "Try-before-you-buy" in AR sets the right expectations for size,
                style, and finish—leading to happier customers and fewer
                post-purchase surprises.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <PhoneCall className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Faster consultations</h3>
              <p className="mt-2 text-gray-300 text-sm">
                Customers arrive with screenshots and measurements from the app.
                Your staff can recommend the right model and close the sale
                sooner.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <BarChart3 className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Actionable insights</h3>
              <p className="mt-2 text-gray-300 text-sm">
                See which products are previewed most in your area and stock
                accordingly. Simple metrics—no data team required.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <ShieldCheck className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Reliable & privacy-first</h3>
              <p className="mt-2 text-gray-300 text-sm">
                Built on modern infrastructure with privacy-minded defaults.
                Your business data stays yours.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <Sparkles className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Simple onboarding</h3>
              <p className="mt-2 text-gray-300 text-sm">
                Add your locations, upload your logo, and you're live. Our team
                can assist with product setup if needed.
              </p>
            </div>
          </div>

          {/* Benefits CTA */}
          <div className="mt-12 flex flex-col items-center gap-3">
            <Link
              to="/retailers/demo"
              className="inline-flex shrink-0 items-center gap-2 rounded-full px-8 py-3 font-medium text-white whitespace-nowrap transition hover:opacity-90"
              style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.3)" }}
            >
              {t("retailers.pricing.founding.cta", "Book a Demo")}
              <Sparkles className="w-4 h-4 shrink-0" />
            </Link>
            <p className="text-sm text-gray-300">
              Already a partner?{" "}
              <Link to="/signin" className="hover:underline" style={{ color: TEAL }}>
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIAL ──────────────────────────────────────────────────────── */}
      {/* PLACEHOLDER — swap quote, name, and store when first real testimonial is collected */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl bg-gray-900 border border-gray-800 px-8 py-10 md:px-12 md:py-12 relative overflow-hidden">
            {/* Large decorative quotation mark */}
            <span
              className="absolute top-4 left-6 text-7xl font-serif leading-none select-none pointer-events-none"
              aria-hidden="true"
              style={{ color: EMBER, opacity: 0.25 }}
            >
              &ldquo;
            </span>

            <blockquote className="relative space-y-5" data-testid="testimonial">
              <p className="text-lg md:text-xl text-gray-200 leading-relaxed font-medium">
                {t(
                  "retailers.testimonial.quote",
                  "Fyrris has changed the way our customers shop for fireplaces. They come in knowing exactly what they want."
                )}
              </p>
              <footer className="text-sm text-gray-400">
                — {t("retailers.testimonial.attribution", "A Founding Member, HPB Specialty Retailer")}
              </footer>
            </blockquote>

            {/* Subtle EMBER glow */}
            <div
              className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-10 pointer-events-none"
              style={{ backgroundColor: EMBER }}
            />
          </div>
        </div>
      </section>

      {/* ── HOW ONBOARDING WORKS ─────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: NAVY }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            {t("retailers.onboarding.title", "How onboarding works")}
          </h2>
          <p className="text-center text-gray-300 mb-14 max-w-xl mx-auto">
            {t("retailers.onboarding.subtitle", "You're live in under three weeks. No developers needed.")}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {onboardingSteps.map(({ icon: Icon, title, desc }, idx) => (
              <div key={title} className="relative flex flex-col gap-5 bg-gray-900 rounded-xl p-6">
                {/* Step number */}
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0"
                  style={{ backgroundColor: EMBER }}
                >
                  {idx + 1}
                </div>

                <Icon className="w-7 h-7" style={{ color: TEAL }} />

                <h3 className="text-xl font-semibold text-white">{title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{desc}</p>

                {/* Connector line (not on last step) */}
                {idx < onboardingSteps.length - 1 && (
                  <div
                    className="hidden md:block absolute top-7 left-full w-8 h-px"
                    style={{ backgroundColor: `${EMBER}44` }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────────── */}
      <section id="pricing" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            {t("retailers.pricing.title", "Pricing tailored to your store")}
          </h2>
          <p className="text-center text-gray-300 mb-14 max-w-xl mx-auto">
            {t("retailers.pricing.subtitle", "Set out in your Order Form, so there are no surprises.")}
          </p>
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {pricingPlans.map((plan) => (
              <div
                key={plan.key}
                className={`relative rounded-2xl flex flex-col bg-white shadow-xl overflow-hidden ${
                  plan.key === "founding" ? "md:scale-[1.02] md:z-10" : ""
                }`}
                style={
                  plan.key === "founding"
                    ? { border: `2px solid ${EMBER}` }
                    : { border: "1px solid #E5E7EB" }
                }
              >
                {/* 1. Badge pill */}
                <div className="px-6 pt-6">
                  <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${plan.badgeClass}`}>
                    {plan.badge}
                  </span>
                </div>

                {/* 2. Plan name */}
                <div className="px-6 pt-3">
                  <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                </div>

                {/* 3. Pricing line: no public prices. Tailored to the store, set out in the Order Form. */}
                <div className="px-6 pt-2">
                  <p className="text-lg font-semibold text-gray-900">{pricingTailored}</p>
                  <p className="text-sm text-gray-500">{pricingOrderForm}</p>
                </div>

                {/* 4. Subtext */}
                {plan.subtext && <p className="px-6 pt-1 text-sm text-gray-400">{plan.subtext}</p>}

                {/* 5. Divider */}
                <div className="mx-6 mt-5 border-t border-gray-100" />

                {/* 6. Feature list */}
                <ul className="px-6 pt-5 flex flex-col gap-3 flex-1">
                  {plan.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <span
                        className="mt-1.5 h-2 w-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: plan.dotColor }}
                      />
                      {feat}
                    </li>
                  ))}
                </ul>

                {/* 8. Divider */}
                <div className="mx-6 mt-4 border-t border-gray-100" />

                {/* 10. CTA */}
                <div className="px-6 pt-4 pb-6">
                  <Link
                    to={plan.ctaHref}
                    className="w-full inline-flex items-center justify-center rounded-full px-10 py-3 text-sm font-semibold text-white whitespace-nowrap transition hover:opacity-90"
                    style={{ backgroundColor: plan.ctaColor }}
                  >
                    {plan.cta}
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Pricing note */}
          <div className="mt-10 mx-auto max-w-md bg-gray-900 rounded-xl p-4 flex items-center gap-3">
            <FileText className="w-5 h-5 flex-shrink-0" style={{ color: TEAL }} />
            <p className="text-sm text-gray-300">
              {t(
                "retailers.pricing.orderFormNote",
                "Your pricing is tailored to your store and set out in your Order Form."
              )}
            </p>
          </div>

          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginTop: '1rem' }}>
            {t("pricing.currencyNote", "Pricing is available in CAD or USD.")}
          </p>

        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: NAVY }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            {t("retailers.faq.title", "Frequently asked questions")}
          </h2>
          <p className="text-center text-gray-300 mb-12">
            {t("retailers.faq.subtitle", "Still have questions? Reach out at support@fyrris.com")}
          </p>

          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, idx) => (
              <AccordionItem
                key={idx}
                value={`item-${idx}`}
                className="border-gray-800"
              >
                <AccordionTrigger className="text-white hover:text-white text-base font-semibold py-5 text-left">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-300 pb-5 leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 text-center" style={{ paddingBottom: "0.5rem" }}>
            <Link
              to={"/retailers/demo"}
              className="transition hover:opacity-90"
              style={{
                backgroundColor: "#FF6B35",
                color: "white",
                borderRadius: "9999px",
                padding: "0.75rem 2.5rem",
                whiteSpace: "nowrap",
                fontWeight: "500",
                width: "auto",
                display: "inline-block",
                boxShadow: "0 4px 14px rgba(255,107,53,0.3)",
              }}
            >
              {t("retailers.faq.cta", "Book a Demo →")}
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
